<template>
  <div id="payment">
    <template v-if="!form.bankTransfer">
      <h3>Card Payment</h3>
      <div class="form-group">
        <p class="py-4 ml-8 mb-0">
          Your payment is processed securely through Stripe. Once your card is confirmed at checkout, a payment
          confirmation is emailed to you straight away and your order goes into production.
        </p>
        <p class="ml-8 pb-4 mb-0">
          <small><i>Prefer to pay another way? Let us know if you'd like to switch to bank transfer.</i></small>
        </p>
      </div>
    </template>
    <template v-else>
      <h3>Bank Transfer Payment</h3>
      <div class="form-group">
        <p class="py-4 ml-8 mb-0">
          When you submit this form, we'll email you Plykea's bank details — including your order number as payment
          reference — so you can make your transfer. Once you've sent it, just let your Quote contact know so we can
          match your payment and move your order into production.
        </p>
        <p class="ml-8 pb-4 mb-0">
          <small><i>Prefer to pay instantly? Let us know if you'd like to switch to card payment.</i></small>
        </p>
      </div>
    </template>
    <div v-if="!form.bankTransfer">
      <h3>Card Details</h3>
      <div class="form-group">
        <v-text-field
          v-model="stripeObjects.cardName"
          label="Name on Card"
          :rules="[$rules.required('Name on Card')]"
          required
        >
        </v-text-field>
        <v-row>
          <v-col cols="11">
            <div class="stripe-input">
              <div class="fancy-label" :class="{ tiny: cardNumberFocussed }">Card Number</div>
              <div ref="cardNumber"></div>
            </div>
          </v-col>
          <v-col cols="1" class="mt-2">
            <transition name="fade">
              <img :src="cardIcons[cardType]" v-if="cardIcons[cardType]" alt="Payment Card Icon" height="20" class="" />
            </transition>
          </v-col>
          <v-col cols="12" class="py-0"
            ><div class="text-error v-messages">
              {{ cardErrors.cardNumber }}
            </div></v-col
          >
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <div class="stripe-input">
              <div class="fancy-label" :class="{ tiny: cardExpiryFocussed }">Expiration Date (MM/YY)</div>
              <div ref="cardExpiry"></div>
            </div>
          </v-col>
          <v-col>
            <div class="stripe-input">
              <div class="fancy-label" :class="{ tiny: cardCvcFocussed }">Security Code</div>
              <div ref="cardCvc"></div>
            </div>
          </v-col>
          <v-col class="mt-2">
            <transition name="fade">
              <img :src="cardIcons.security" v-if="cardIcons.security" alt="Payment Card Icon" height="20" />
            </transition>
          </v-col>
          <v-col cols="12" md="6" class="py-0"
            ><div class="text-error v-messages">
              {{ cardErrors.cardExpiry }}
            </div></v-col
          >
          <v-col cols="12" md="6" class="py-0"
            ><div class="text-error v-messages">
              {{ cardErrors.cardCvc }}
            </div></v-col
          >
        </v-row>
      </div>
    </div>
    <v-form ref="form" id="order-form" validate-on="blur">
      <h3 class="mt-4">Billing Address</h3>
      <div class="form-group">
        <v-switch
          :label="
            form.billingSameAsDelivery
              ? 'Toggle to use different billing address'
              : 'Toggle to use same as delivery address'
          "
          v-model="form.billingSameAsDelivery"
          @update:model-value="resetBillingAddress"
          hide-details
        ></v-switch>
        <transition name="fade">
          <!-- ADDRESS FORM BILLING -->
          <div v-show="!form.billingSameAsDelivery" ref="billing-address-element"></div>
        </transition>
        <!-- error display -->
        <p v-if="billingAddressError" class="text-red">
          {{ billingAddressError }}
        </p>
      </div>
      <h3 class="mt-4">Sign Off</h3>
      <div class="form-group">
        <v-checkbox
          class="sign-off"
          v-model="form.approved"
          label="I confirm I have checked the items contained within the original quotation are correct. I understand that any changes required after placing my order may incur additional costs and / or delay my order"
          color="accent"
          :rules="[$rules.checked('Customer Approval')]"
        ></v-checkbox>
      </div>
    </v-form>
    <div class="mt-5" style="display: flex; justify-content: space-between">
      <v-btn color="primary" variant="text" class="mx-0 px-0" @click="$emit('back')"
        ><v-icon>mdi-chevron-left</v-icon>Back to information</v-btn
      >
      <v-btn color="accent" variant="flat" class="mx-0" @click="submit">{{
        form.bankTransfer ? 'Continue' : 'Pay'
      }}</v-btn>
    </div>
  </div>
</template>

<script>
import { validateUsZipcode } from '@/js/validators'

import monodefault from 'payment-icons/min/mono/default.svg'
import security from 'payment-icons/min/mono/security-code.svg'
import visa from 'payment-icons/min/single/visa.svg'
import mastercard from 'payment-icons/min/flat/mastercard.svg'
import amex from 'payment-icons/min/flat/amex.svg'
import discover from 'payment-icons/min/flat/discover.svg'
import diners from 'payment-icons/min/flat/diners.svg'
import jcb from 'payment-icons/min/flat/jcb.svg'
import unionpay from 'payment-icons/min/flat/unionpay.svg'

export default {
  name: 'PaymentUS',
  props: ['form', 'elements', 'stripeObjects'],
  data() {
    return {
      billingAddressError: null,
      cardErrors: {},
      cardIcons: {
        visa: visa,
        mastercard: mastercard,
        amex: amex,
        discover: discover,
        diners: diners,
        jcb: jcb,
        unionpay: unionpay,
        unknown: monodefault,
        security: security
      },
      stripeElementBaseStyle: {
        base: {
          fontSize: '16px',
          fontFamily: 'proxima-nova, sans-serif',
          fontSmoothing: 'antialiased',
          letterSpacing: '0.5px'
        }
      },
      cardNumberFocussed: false,
      cardExpiryFocussed: false,
      cardCvcFocussed: false,
      cardType: 'unknown',
      billingAddressDetails: null,
      googleMapsApiRetrieved: false
    }
  },
  mounted() {
    this.mountBillingAddress()
    this.mountStripe()
  },
  beforeUnmount() {
    this.unmountStripe()
    this.unmountBillingAddress()
  },
  watch: {
    'form.bankTransfer'(val) {
      // QT may flip the order's payment method mid-checkout; mount/unmount accordingly
      if (val) {
        this.unmountStripe()
      } else {
        this.mountStripe()
      }
    }
  },
  computed: {
    cardComplete() {
      return (
        Object.keys(this.cardErrors).length == 0 &&
        this.stripeObjects.cardNumber &&
        this.stripeObjects.cardNumber._complete &&
        this.stripeObjects.cardExpiry &&
        this.stripeObjects.cardExpiry._complete &&
        this.stripeObjects.cardCvc &&
        this.stripeObjects.cardCvc._complete
      )
    }
  },
  methods: {
    async mountStripe() {
      await this.$nextTick()
      // Card form is hidden when BACS — nothing to mount
      if (this.form.bankTransfer) return
      if (!this.$refs.cardNumber) return

      this.setupStripeElement('cardNumber', 'cardNumberFocussed')
      this.setupStripeElement('cardExpiry', 'cardExpiryFocussed')
      this.setupStripeElement('cardCvc', 'cardCvcFocussed')
    },
    unmountStripe() {
      this.unmountStripeElement('cardNumber')
      this.unmountStripeElement('cardExpiry')
      this.unmountStripeElement('cardCvc')
    },
    setupStripeElement(el, focus) {
      if (this.stripeObjects[el]) {
        try {
          this.stripeObjects[el].destroy()
        } catch (e) {
          /* ignore */
        }
        this.stripeObjects[el] = null
      }
      this.stripeObjects[el] = this.elements.create(el, {
        style: this.stripeElementBaseStyle,
        placeholder: ''
      })
      this.stripeObjects[el].mount(this.$refs[el])

      this.stripeObjects[el].addEventListener('change', (event) => {
        if (event.brand) this.cardType = event.brand

        if (event.error) {
          if (!this.cardErrors[event.elementType]) {
            this.cardErrors[event.elementType] = event.error.message
          } else {
            this.cardErrors[event.elementType] = event.error.message
          }
        } else if (this.cardErrors[event.elementType]) {
          delete this.cardErrors[event.elementType]
        }
      })
      this.stripeObjects[el].addEventListener('focus', () => {
        this[focus] = true
      })
      this.stripeObjects[el].addEventListener('blur', () => {
        this[focus] = !this.stripeObjects[el]._empty
      })
    },
    unmountStripeElement(el) {
      if (!this.stripeObjects[el]) return
      try {
        this.stripeObjects[el].destroy()
        this.stripeObjects[el].removeEventListener('change')
        this.stripeObjects[el].removeEventListener('focus')
        this.stripeObjects[el].removeEventListener('blur')
      } catch (e) {
        /* ignore */
      }
      this.stripeObjects[el] = null
    },
    resetBillingAddress() {
      if (this.form.billingSameAsDelivery) {
        this.billingAddressDetails = null
        this.unmountBillingAddress()
      } else {
        let billingElement = this.elements.getElement('address')
        if (!billingElement) {
          this.mountBillingAddress()
        }
      }
    },
    mountBillingAddress() {
      this.setupBillingAddressElement('billing-address-element')
    },
    unmountBillingAddress() {
      this.unmountBillingAddressElement('billing-address-element')
    },
    setupBillingAddressElement(el) {
      let allowedCountriesArray = ['US']
      let placesKey
      if (window.location.hostname == 'mega.plykea.com') {
        placesKey = 'AIzaSyAv4aRIjITY5teVksdZiTZkJiYW2-AtkjU' //prod
      } else {
        placesKey = 'AIzaSyCIUq0Y4cXiT9oIsPN4JReKvOS1C5QQf58' //dev
      }
      let addressOptions = {
        mode: 'billing',
        autocomplete: {
          mode: 'google_maps_api',
          apiKey: placesKey
        },
        allowedCountries: allowedCountriesArray,
        blockPoBox: true,
        display: {
          name: 'split'
        }
      }
      const stale = this.elements.getElement('address')
      if (stale) {
        try {
          stale.destroy()
        } catch (e) {
          /* already destroyed */
        }
      }
      this.stripeObjects[el] = this.elements.create('address', addressOptions)
      this.stripeObjects[el].mount(this.$refs[el])
      this.stripeObjects[el].on('change', (event) => {
        if (event.complete) {
          this.billingAddressDetails = event.value
        }
      })
    },
    unmountBillingAddressElement(el) {
      if (!this.stripeObjects[el]) return
      try {
        this.stripeObjects[el].destroy()
      } catch (e) {
        /* already destroyed */
      }
      this.stripeObjects[el] = null
    },
    async submit() {
      try {
        if (!this.form.billingSameAsDelivery) {
          const billingElement = this.elements.getElement('address')
          const billingResult = await billingElement.getValue()

          if (
            billingResult.complete &&
            billingResult.value.address.postal_code &&
            billingResult.value.address.line1 &&
            billingResult.value.address.city &&
            billingResult.value.address.state &&
            billingResult.value.address.country === 'US' // Ensure it's a US address
          ) {
            this.billingAddressError = null // Clear error if all required fields are filled
            this.billingAddressDetails = billingResult.value
          } else {
            this.billingAddressError = 'Please complete all required billing address fields'
            return
          }

          // Validate postcode explicitly here:
          const isZipcodeValid = await validateUsZipcode(
            this.billingAddressDetails.address.postal_code,
            this.billingAddressDetails.address.state
          )
          if (!isZipcodeValid) {
            this.billingAddressError = 'Invalid US zipcode'
            return
          } else {
            this.billingAddressError = null // Clear error if zipcode is valid
          }

          // Run other validations, the sign-off and card details
          const isValid = await this.$utils.validateForm(this.$refs.form)
          if (isValid && (this.cardComplete || this.form.bankTransfer)) {
            this.$emit('success', this.form, this.billingAddressDetails)
          } else {
            return
          }
        } else {
          // Just validate the sign-off and card details
          const isValid = await this.$utils.validateForm(this.$refs.form)
          if (isValid && (this.cardComplete || this.form.bankTransfer)) {
            // billing address will be set as delivery address at Checkout.vue
            this.$emit('success', this.form)
          }
        }
      } catch (error) {
        console.error('Submit error:', error)
        return
      }
    }
  }
}
</script>

<style lang="scss">
#payment {
  .stripe-input {
    position: relative;
    margin-top: 16px;
    border-bottom: solid 1px #949494;
    .fancy-label {
      font-size: 16px;
      position: absolute;
      top: 5px;
      opacity: 0.7;
      transition: transform 0.25s, opacity 0.5s;
      transform-origin: top left;
      &.tiny {
        opacity: 1;
        transform: scale(0.7) translate(0, -25px);
      }
    }
  }
  .sign-off {
    .v-input__slot {
      align-items: flex-start;
    }
  }
  .StripeElement {
    padding-top: 8px;
    padding-bottom: 8px;
  }
  .v-switch {
    margin: 10px 0;
  }
  .v-checkbox {
    margin-top: 5px;
  }
}
</style>
